import { assign, createMachine, fromPromise } from "xstate";
import { deleteCookie, setSadCookie } from "../../../utils/cookies";
import {
	checkAuth,
	enquireAboutDog,
	fetchAccountProfile,
	getValidCachedProfile,
	handleLogout,
	handleProfileSync,
	updateLocalAccountProfile,
} from "./utils/Account.utils";
import { getOfflineProfile, updateOfflineProfile } from "./utils/Offline.utils";
import {
	addToBasket,
	addToFavourites,
	addToInterested,
	getInitialProfile,
	removeFromBasket,
	removeFromFavourites,
	removeFromInterested,
	updateAmount,
	updateRecipient,
} from "./utils/Profile.utils";
import { DEFAULT_PROFILE, UNINITIALISED_PROFILE } from "./utils/consts";

/**
 * Types for event data parameters to improve type safety
 */
export type FavouritesData = {
	favouriteId?: string;
	timestamp?: number;
	source?: string;
};

export type EnquiryData = {
	enquiryId?: string;
	timestamp?: number;
	source?: string;
	message?: string;
	contactDetails?: {
		name?: string;
		email?: string;
		phone?: string;
	};
};

/**
 * Type definitions for the MyAccount state machine
 */
export type MyAccountContext = {
	profile: DogsTrust.Profile | null;
	status: DogsTrust.MyAccountAuthState;
	isLoading: boolean;
	error?: {
		state: string;
		message: string | Error;
		action?: string; // Add information about which action failed
	};
	syncRetryCount?: number;
	isAuthenticated: boolean;
	eventBuffer: Array<MyAccountEvent>; // Buffer for events that can't be processed immediately
	isOnline: boolean; // Track network connectivity status
	shouldClearBasket?: boolean; // Flag to clear basket cookie
};

/**
 * The events that can be sent to the MyAccount state machine
 */
export type MyAccountEvent =
	| { type: "START"; clearBasketCookie?: boolean }
	| { type: "RETRY_AUTH" }
	| { type: "LOGIN" }
	| { type: "LOGOUT" }
	| { type: "SESSION_EXPIRED" } // Add this new event type
	| { type: "SYNC_COMPLETE"; profile: DogsTrust.Profile } // Event to handle external profile sync
	| { type: "REFRESH_PROFILE" } // Event to trigger a fresh profile fetch
	| { type: "ADD_TO_BASKET"; dog: DogsTrust.SADCookie }
	| { type: "REMOVE_FROM_BASKET"; dog: DogsTrust.SADCookie; index?: number }
	| { type: "UPDATE_RECIPIENT"; basketKey: number; isGift: boolean }
	| { type: "UPDATE_AMOUNT"; amount: number }
	| {
			type: "ADD_TO_FAVOURITES";
			dog: DogsTrust.DogData;
			data: DogsTrust.DogDataTracking;
	  }
	| {
			type: "REMOVE_FROM_FAVOURITES";
			dog: DogsTrust.DogData;
			data: DogsTrust.DogDataTracking;
	  }
	| { type: "ADD_TO_INTERESTED"; dog: DogsTrust.DogData }
	| { type: "REMOVE_FROM_INTERESTED"; dog: DogsTrust.DogData }
	| {
			type: "ENQUIRE_ABOUT_DOG";
			dog: DogsTrust.DogData;
			data: DogsTrust.DogDataTracking;
	  }
	| { type: "CLEAR_BASKET" }
	| { type: "CLEAR_BASKET_COOKIE_DETECTED" };

const eventsToBuffer = {
	ADD_TO_BASKET: { actions: "bufferEvent" },
	REMOVE_FROM_BASKET: { actions: "bufferEvent" },
	UPDATE_RECIPIENT: { actions: "bufferEvent" },
	UPDATE_AMOUNT: { actions: "bufferEvent" },
	ADD_TO_FAVOURITES: { actions: "bufferEvent" },
	REMOVE_FROM_FAVOURITES: { actions: "bufferEvent" },
	ADD_TO_INTERESTED: { actions: "bufferEvent" },
	REMOVE_FROM_INTERESTED: { actions: "bufferEvent" },
	ENQUIRE_ABOUT_DOG: { actions: "bufferEvent" },
	CLEAR_BASKET: { actions: "bufferEvent" },
	CLEAR_BASKET_COOKIE_DETECTED: { actions: "bufferEvent" },
};
/**
 * MyAccount state machine definition
 *
 * This machine manages the authentication state and profile for the MyAccount feature.
 * It handles various flows including:
 * - Authentication
 * - Profile loading and merging
 * - Profile syncing with server
 * - User actions (basket operations, favorites, interests, etc.)
 */
export const myAccountMachine = createMachine(
	{
		id: "myAccount",
		initial: "initializing",
		context: {
			profile: getInitialProfile(),
			status: "unknown" as DogsTrust.MyAccountAuthState,
			isLoading: true,
			error: undefined,
			isAuthenticated: false,
			eventBuffer: [], // Initialize empty event buffer
			isOnline: true, // Initialize as online
		} satisfies MyAccountContext,
		types: {
			context: {} as MyAccountContext,
			events: {} as MyAccountEvent,
			input: {} as {
				profile?: DogsTrust.Profile;
				status?: DogsTrust.MyAccountAuthState;
			},
		},

		states: {
			// Initial state - will always move to checkingLocalCache
			initializing: {
				on: {
					START: {
						target: "checkingLocalCache",
						actions: assign({
							shouldClearBasket: ({ event }) =>
								event.clearBasketCookie ?? false,
							isLoading: true,
						}),
					},
					...eventsToBuffer,
				},
			},

			// New state to check if we have a valid cached profile before checking auth
			checkingLocalCache: {
				entry: assign({
					isLoading: true,
				}),
				// Buffer all user events while checking cache
				on: {
					...eventsToBuffer,
					LOGOUT: {
						target: "loggingOut",
						actions: ["clearEventBuffer"],
					},
				},
				invoke: {
					id: "checkingLocalCache",
					src: fromPromise(async () => getValidCachedProfile()),
					onDone: [
						{
							// If we have a valid cached profile and shouldClearBasket is true
							guard: ({ context, event }) =>
								!!event.output && context.shouldClearBasket,
							actions: [
								assign({
									profile: ({ event }) => {
										deleteCookie("clear_basket", true);
										setSadCookie([], event.output?.amount || 2);
										return {
											...event.output,
											amount: event.output?.amount || 2,
											fromCache: true,
											basket: [],
										};
									},
									status: "authorized" as const,
									isAuthenticated: true,
									isLoading: false,
									// Reset the flag after use
									shouldClearBasket: false,
								}),
							],
							target: "syncingAccountProfile", // Go to sync immediately
						},
						{
							// If we have a valid cached profile but don't need to clear basket
							guard: ({ event }) => !!event.output,
							actions: assign({
								profile: ({ event }) => ({ ...event.output, fromCache: true }),
								status: "authorized" as const,
								isAuthenticated: true,
								isLoading: false,
							}),
							target: "userLoggedIn",
						},
						{
							// Otherwise proceed with normal auth flow
							target: "checkingAuth",
						},
					],
					onError: {
						// If there's an error checking cache, proceed with normal auth flow
						target: "checkingAuth",
						actions: ({ event }) => {
							console.error("🐛 Error checking local cache:", event.error);
						},
					},
				},
			},

			// State for checking authentication - always moves to determiningProfile
			checkingAuth: {
				entry: assign({
					isLoading: true,
					status: "authenticating" as const,
				}),
				// Buffer all user events while checking auth
				on: {
					...eventsToBuffer,

					LOGOUT: {
						target: "loggingOut",
						actions: ["clearEventBuffer"],
					},
				},

				invoke: {
					id: "checkAuth",
					src: fromPromise(checkAuth),
					onDone: {
						target: "determiningProfile",
						actions: assign({
							status: ({ event }) => event?.output,
							isAuthenticated: ({ event }) => event?.output === "authorized",
						}),
					},
					onError: {
						target: "error",
						actions: assign({
							error: ({ event }) => ({
								state: "checkingAuth",
								message: event.error as Error,
							}),
						}),
					},
				},
			},

			// State for determining the profile -
			// Will move to fetchingAccountProfile if authorized
			// or loadingOfflineProfile if unauthorized
			determiningProfile: {
				// Buffer all user events while determining profile
				on: {
					...eventsToBuffer,

					LOGOUT: {
						target: "loggingOut",
						actions: ["clearEventBuffer"],
					},
				},
				always: [
					{
						guard: ({ context }) => context.status === "authorized",
						target: "fetchingAccountProfile",
					},
					{
						target: "loadingOfflineProfile",
					},
				],
			},

			// Fetches the account profile from the server
			// If successful it handles the merge of the offline profile
			fetchingAccountProfile: {
				// Buffer all user events while fetching account profile
				on: {
					...eventsToBuffer,

					LOGOUT: {
						target: "loggingOut",
						actions: ["clearEventBuffer"],
					},
				},
				invoke: {
					id: "fetchingAccountProfile",
					src: fromPromise(fetchAccountProfile),
					onDone: [
						{
							// Handle the case when fetchedProfile is null
							guard: ({ event }) => event.output === null,
							actions: assign({
								status: "failed-profile-fetch",
								error: {
									state: "fetchingAccountProfile",
									message: "Failed to fetch profile",
								},
								isLoading: false,
							}),
							target: "error",
						},
						{
							// Handle when the profile should be merged AND the basket should be cleared
							guard: ({ context, event }) =>
								(event.output as DogsTrust.Profile)?.shouldSync &&
								context.shouldClearBasket,
							actions: [
								assign({
									isLoading: false,
									profile: ({ event: { output: fetchedProfile } }) => {
										deleteCookie("clear_basket", true);
										setSadCookie([], fetchedProfile?.amount || 2);
										return {
											...fetchedProfile,
											amount: fetchedProfile?.amount || 2,
											fromCache: false,
											basket: [],
										};
									},
									// Reset the flag after use
									shouldClearBasket: false,
								}),
							],
							target: "syncingAccountProfile",
						},
						{
							// Handle when the profile should be merged (without basket clearing)
							guard: ({ event }) =>
								(event.output as DogsTrust.Profile)?.shouldSync,
							actions: [
								assign({
									isLoading: false,
									profile: ({ event: { output: fetchedProfile } }) => ({
										...fetchedProfile,
										fromCache: false,
									}),
								}),
							],
							target: "syncingAccountProfile",
						},
						{
							// Handle the normal case when profile is found AND basket should be cleared
							guard: ({ context }) => context.shouldClearBasket,
							actions: [
								assign({
									isLoading: false,
									profile: ({ event: { output: fetchedProfile } }) => {
										deleteCookie("clear_basket", true);
										setSadCookie([], fetchedProfile?.amount || 2);

										return {
											...fetchedProfile,
											amount: fetchedProfile?.amount || 2,
											fromCache: false,
											basket: [],
										};
									},
									// Reset the flag after use
									shouldClearBasket: false,
								}),
							],
							target: "syncingAccountProfile", // Go straight to sync
						},
						{
							// Handle the normal case when profile is found (without basket clearing)
							actions: [
								assign({
									isLoading: false,
									profile: ({ event: { output: fetchedProfile } }) => ({
										...fetchedProfile,
										fromCache: false,
									}),
								}),
							],
							target: "userLoggedIn",
						},
					],
					onError: {
						target: "error",
						actions: assign({
							error: ({ event }) => ({
								state: "fetchingAccountProfile",
								message: event.error as Error,
							}),
						}),
					},
				},
			},

			// This is the ideal state an authenticated user should be in and all actions can be triggered from here
			userLoggedIn: {
				entry: [
					"processEventBuffer",
					assign({ eventBuffer: [] }), // Clear the buffer after processing
				],

				on: {
					// Add SESSION_EXPIRED event handler
					SESSION_EXPIRED: {
						target: "checkingAuth",
						actions: assign({
							isLoading: true,
							// Don't clear the profile yet - we'll let the auth check determine what to do
						}),
					},
					// Event to handle external profile sync
					SYNC_COMPLETE: {
						actions: assign({
							profile: ({ event }) => event.profile,
						}),
					},
					REFRESH_PROFILE: {
						target: "fetchingAccountProfile",
					},
					ADD_TO_BASKET: {
						actions: assign({
							profile: ({ context, event }) =>
								addToBasket(event.dog, context.profile),
						}),
						target: "syncingAccountProfile", // Immediately transition to syncingAccountProfile
					},
					REMOVE_FROM_BASKET: {
						actions: assign({
							profile: ({ context, event }) =>
								removeFromBasket(event.dog, context.profile, event.index),
						}),
						target: "syncingAccountProfile", // Immediately transition to syncingAccountProfile
					},
					UPDATE_RECIPIENT: {
						actions: assign({
							profile: ({ context, event }) =>
								updateRecipient(event.basketKey, event.isGift, context.profile),
						}),
						target: "syncingAccountProfile", // Immediately transition to syncingAccountProfile
					},
					UPDATE_AMOUNT: {
						actions: assign({
							profile: ({ context, event }) =>
								updateAmount(event.amount, context.profile),
						}),
						target: "syncingAccountProfile", // Immediately transition to syncingAccountProfile
					},
					ADD_TO_FAVOURITES: {
						actions: assign({
							profile: ({ context: { profile }, event: { dog, data } }) =>
								addToFavourites(dog, data, profile),
						}),
						target: "syncingAccountProfile", // Immediately transition to syncingAccountProfile
					},
					REMOVE_FROM_FAVOURITES: {
						actions: assign({
							profile: ({ context: { profile }, event: { data, dog } }) =>
								removeFromFavourites(dog, data, profile),
						}),
						target: "syncingAccountProfile", // Immediately transition to syncingAccountProfile
					},
					ADD_TO_INTERESTED: {
						actions: assign({
							profile: ({ context, event: { dog } }) =>
								addToInterested(dog, context.profile),
						}),
						target: "syncingAccountProfile", // Immediately transition to syncingAccountProfile
					},
					REMOVE_FROM_INTERESTED: {
						actions: assign({
							profile: ({ context, event }) =>
								removeFromInterested(event.dog, context.profile),
						}),
						target: "syncingAccountProfile", // Immediately transition to syncingAccountProfile
					},
					ENQUIRE_ABOUT_DOG: {
						actions: [
							assign({
								profile: ({ context, event: { dog, data } }) =>
									enquireAboutDog(dog, data, context.profile),
							}),
						],
						target: "syncingAccountProfile", // Immediately transition to syncingAccountProfile
					},
					CLEAR_BASKET: {
						actions: assign({
							profile: ({ context }) => ({
								...context.profile,
								basket: [],
							}),
						}),
						target: "syncingAccountProfile", // Immediately transition to syncingAccountProfile
					},
					CLEAR_BASKET_COOKIE_DETECTED: {
						actions: [
							assign({
								profile: ({ context }) => {
									deleteCookie("clear_basket", true);
									setSadCookie([], context.profile?.amount || 2);

									return {
										...context.profile,
										amount: context.profile?.amount || 2,
										basket: [],
									};
								},
								shouldClearBasket: false, // Reset the flag
							}),
						],
						target: "syncingAccountProfile", // Immediately transition to syncingAccountProfile
					},

					LOGOUT: {
						target: "loggingOut",
						actions: ["clearEventBuffer"],
					},
					RETRY_AUTH: {
						target: "checkingAuth",
						actions: assign({
							isLoading: true,
						}),
					},
				},
			},

			syncingAccountProfile: {
				entry: [
					assign({
						// Don't reset the retry count on entry, only pass through existing value
						isLoading: true, // Set loading state when entering sync
					}),
				],
				always: [
					{
						// If we're offline, transition to userOffline
						guard: ({ context }) => !context.isOnline,
						target: "userOffline",
						actions: assign({
							error: {
								state: "syncingAccountProfile",
								message: new Error("Cannot sync while offline"),
							},
						}),
					},
					{
						guard: ({ context }) => context.status === "unauthorized",
						target: "userOffline",
					},
					{
						guard: ({ context }) => (context.syncRetryCount || 0) >= 5,
						target: "error",
						actions: assign({
							error: {
								state: "syncingAccountProfile",
								message: new Error("Sync failed after multiple attempts"),
							},
							isLoading: false,
						}),
					},
				],

				// Buffer user events while syncing
				on: {
					...eventsToBuffer,

					LOGOUT: {
						target: "loggingOut",
						actions: ["clearEventBuffer"],
					},
				},
				invoke: {
					id: "syncingAccountProfile",
					input: ({ context }) => ({
						profile: context.profile,
						status: context.status,
						syncRetryCount: context.syncRetryCount || 0,
					}),
					src: fromPromise(
						async ({
							input,
						}: {
							input: {
								profile: DogsTrust.Profile;
								status: DogsTrust.MyAccountAuthState;
								syncRetryCount: number;
							};
						}) => await handleProfileSync(input.profile),
					),
					onDone: {
						actions: [
							assign({
								profile: ({ event }) => ({
									...event.output,
								}),
								syncRetryCount: 0, // Reset retry count on success
								isLoading: false, // Set to false when sync is complete
							}),
							// Add action to update local cache after successful sync
							({ event }) => {
								const { output: profile } = event;
								// Import needs to be added at the top: import { updateLocalAccountProfile } from "./utils/Account.utils";
								if (profile) {
									updateLocalAccountProfile(profile);
								}
							},
						],
						target: "userLoggedIn",
					},
					onError: {
						// On error, go to syncRetryDelay instead of immediately to error state
						target: "syncRetryDelay",
						actions: assign({
							syncRetryCount: ({ context }) =>
								(context.syncRetryCount || 0) + 1,
							error: ({ event }) => ({
								state: "syncingAccountProfile",
								message: event.error as Error,
							}),
						}),
					},
				},
			},
			syncRetryDelay: {
				entry: [
					assign({
						isLoading: false, // Don't show loading state during delay
					}),
				],
				// Buffer user events while waiting for retry
				on: {
					...eventsToBuffer,
					LOGOUT: {
						target: "loggingOut",
						actions: ["clearEventBuffer"],
					},
				},
				// Exponential backoff: 2^retry * 1000ms (2s, 4s, 8s, 16s, 32s)
				after: {
					// Dynamic delay based on retry count
					SYNC_RETRY_DELAY: {
						target: "syncingAccountProfile",
						actions: assign({
							isLoading: true, // Set loading state when retrying
						}),
					},
				},
				exit: [
					({ context }) => {
						console.log(
							`🔄 Retrying sync after backoff, attempt ${context.syncRetryCount}`,
						);
					},
				],
			},
			loggingOut: {
				invoke: {
					id: "loggingOut",
					src: fromPromise(handleLogout),
					// This is a dead end state - it will redirect to the logout url and redirect back to an offline state
				},
			},

			loadingOfflineProfile: {
				// Buffer all user events while loading offline profile
				on: {
					...eventsToBuffer,

					LOGOUT: {
						target: "loggingOut",
						actions: ["clearEventBuffer"],
					},
				},
				invoke: {
					id: "loadingOfflineProfile",

					src: fromPromise(async () => getOfflineProfile()),
					onDone: [
						{
							// Uninitialised profile AND shouldClearBasket
							guard: ({ context, event }) =>
								(event.output as DogsTrust.Profile)?.type === "uninitialised" &&
								context.shouldClearBasket,
							actions: [
								assign({
									profile: () => {
										deleteCookie("clear_basket", true);
										setSadCookie([], 2); // Reset to default amount

										return {
											...DEFAULT_PROFILE,
											basket: [],
										};
									},
									isLoading: false,
									// Reset the flag after use
									shouldClearBasket: false,
								}),
							],
							target: "updatingOfflineProfile", // Sync the empty basket
						},
						{
							// Normal uninitialised profile case
							guard: ({ event }) =>
								(event.output as DogsTrust.Profile)?.type === "uninitialised",
							actions: [
								assign({
									profile: DEFAULT_PROFILE,
									isLoading: false,
								}),
							],
							target: "userOffline",
						},
						{
							// Existing profile AND shouldClearBasket
							guard: ({ context }) => context.shouldClearBasket,
							actions: [
								assign({
									profile: ({ event }) => {
										deleteCookie("clear_basket", true);
										setSadCookie([], event.output?.amount || 2);
										return {
											...event.output,
											amount: event.output?.amount || 2,
											basket: [],
										};
									},
									isLoading: false,
									// Reset the flag after use
									shouldClearBasket: false,
								}),
							],
							target: "updatingOfflineProfile", // Sync the empty basket
						},
						{
							// Normal existing profile case
							actions: [
								assign({
									profile: ({ event }) => event.output,
									isLoading: false,
								}),
							],
							target: "userOffline",
						},
					],
					onError: {
						target: "error",
						actions: assign({
							error: ({ event }) => ({
								state: "loadingOfflineProfile",
								message: event.error as Error,
							}),
						}),
					},
				},
			},
			userOffline: {
				on: {
					ADD_TO_BASKET: {
						actions: assign({
							profile: ({ context, event: { dog } }) =>
								addToBasket(dog, context.profile),
						}),
						target: "updatingOfflineProfile",
					},
					REMOVE_FROM_BASKET: {
						actions: assign({
							profile: ({ context, event: { dog, index } }) =>
								removeFromBasket(dog, context.profile, index),
						}),
						target: "updatingOfflineProfile",
					},
					UPDATE_RECIPIENT: {
						actions: assign({
							profile: ({ context, event: { basketKey, isGift } }) =>
								updateRecipient(basketKey, isGift, context.profile),
						}),
						target: "updatingOfflineProfile",
					},
					UPDATE_AMOUNT: {
						actions: assign({
							profile: ({ context, event: { amount } }) =>
								updateAmount(amount, context.profile),
						}),
						target: "updatingOfflineProfile",
					},
					ADD_TO_FAVOURITES: {
						actions: assign({
							profile: ({ context, event: { dog, data } }) =>
								addToFavourites(dog, data, context.profile),
						}),
						target: "updatingOfflineProfile",
					},
					REMOVE_FROM_FAVOURITES: {
						actions: assign({
							profile: ({ context, event: { dog, data } }) =>
								removeFromFavourites(dog, data, context.profile),
						}),
						target: "updatingOfflineProfile",
					},
					ADD_TO_INTERESTED: {
						actions: assign({
							profile: ({ context, event: { dog } }) =>
								addToInterested(dog, context.profile),
						}),
						target: "updatingOfflineProfile",
					},
					REMOVE_FROM_INTERESTED: {
						actions: assign({
							profile: ({ context, event: { dog } }) =>
								removeFromInterested(dog, context.profile),
						}),
						target: "updatingOfflineProfile",
					},
					CLEAR_BASKET: {
						actions: assign({
							profile: ({ context }) => ({
								...context.profile,
								basket: [] as DogsTrust.SADCookie[],
							}),
						}),
						target: "updatingOfflineProfile",
					},
					CLEAR_BASKET_COOKIE_DETECTED: {
						actions: [
							assign({
								profile: ({ context }) => {
									deleteCookie("clear_basket", true);
									setSadCookie([], context.profile?.amount || 2);

									return {
										...context.profile,
										amount: context.profile?.amount || 2,
										basket: [],
									};
								},
								shouldClearBasket: false, // Reset the flag
							}),
						],
						target: "updatingOfflineProfile", // Immediately transition to update profile
					},

					LOGIN: {
						target: "checkingAuth",
						actions: assign({
							isLoading: true,
						}),
					},
				},
			},
			updatingOfflineProfile: {
				// Add a guard to check if we have a valid profile before updating
				always: {
					guard: ({ context }) =>
						!context.profile || context.profile === UNINITIALISED_PROFILE,
					target: "error",
					actions: assign({
						error: {
							state: "updatingOfflineProfile",
							message: new Error(
								"Cannot update offline profile: Invalid profile state",
							),
						},
						isLoading: false,
					}),
				},
				// Buffer all user events while updating offline profile
				on: {
					...eventsToBuffer,
					LOGOUT: {
						target: "loggingOut",
						actions: ["clearEventBuffer"],
					},
				},

				invoke: {
					id: "updatingOfflineProfile",
					input: ({ context }) => ({
						profile: context.profile,
					}),
					src: fromPromise(
						async ({
							input: { profile },
						}: {
							input: {
								profile: DogsTrust.Profile;
							};
						}) => updateOfflineProfile(profile),
					),
					onDone: {
						target: "userOffline",
					},
					onError: {
						target: "error",
						actions: assign({
							error: ({ event }) => ({
								state: "updatingOfflineProfile",
								message: event.error as Error,
							}),
							isLoading: false,
						}),
					},
				},
			},
			error: {
				entry: [
					({ context }) => {
						console.error(
							`🐛 ${context.error.state} - ${context.error.message}`,
						);
					},
				],
				always: [
					// Specific recovery paths based on error origin
					{
						// If error happened during auth check, fall back to offline mode
						guard: ({ context }) => context.error?.state === "checkingAuth",
						target: "loadingOfflineProfile",
					},
					{
						// If error happened during account profile fetch, retry with offline mode
						guard: ({ context }) =>
							context.error?.state === "fetchingAccountProfile",
						target: "loadingOfflineProfile",
					},
					{
						// If error happened during profile sync, return to previous authenticated state but with sync reset
						guard: ({ context }) =>
							context.error?.state === "syncingAccountProfile",
						actions: assign({
							syncRetryCount: 0,
							isLoading: false,
						}),
						target: "checkingAuth",
					},
					{
						// If error happened during logout, force to offline mode
						guard: ({ context }) => context.error?.state === "loggingOut",
						actions: assign({
							profile: DEFAULT_PROFILE,
							status: "unauthorized" as const,
							isAuthenticated: false,
							isLoading: false,
						}),
						target: "userOffline",
					},
				],
				on: {
					// Allow manual recovery options
					RETRY_AUTH: "checkingAuth",
					LOGIN: "checkingAuth",
					LOGOUT: {
						target: "userOffline",
						actions: [
							assign({
								profile: DEFAULT_PROFILE,
								status: "unauthorized" as const,
								isAuthenticated: false,
								isLoading: false,
							}),
							"clearEventBuffer",
						],
					},
				},
			},
		},
	},
	{
		actions: {
			// Action to add an event to the buffer
			bufferEvent: assign({
				eventBuffer: ({ context, event }) => [...context.eventBuffer, event],
			}),
			// Action to process buffered events
			processEventBuffer: ({ context: { eventBuffer }, self: { send } }) => {
				if (eventBuffer.length > 0) {
					// Process each event in the buffer using setTimeout to ensure
					// they're processed after the current transition completes
					setTimeout(() => {
						for (const bufferedEvent of eventBuffer) {
							send(bufferedEvent);
						}
					}, 0);
				}
			},
			// Action to clear the event buffer
			clearEventBuffer: assign({
				eventBuffer: [] as Array<MyAccountEvent>,
			}),
		},
		// Define delays that match the state delay names
		delays: {
			SYNC_RETRY_DELAY: ({ context }) => {
				// Exponential backoff with jitter: 2^retry * 1000ms (2s, 4s, 8s, 16s, 32s) + random jitter
				const baseDelay = 2 ** (context.syncRetryCount || 0) * 1000;
				const jitter = Math.random() * 500; // Add up to 500ms of random jitter
				return baseDelay + jitter;
			},
		},
	},
);

// No batch processor implementation - reverted to direct sync approach
